import Bugsnag from '@bugsnag/js';
import * as jose from 'jose';
import {
    Currency,
    StopayPaymentMethod,
    ConfigurationProps,
} from '../app/checkout/models';
import { MfeException } from '../app/errors/mfe-error.exception';
import {
    BankTransferSystem,
    BankTransferSystemInfo,
    CountryName,
    TransactionType,
    Processor,
    PROCESSOR_ID_MAP,
    ProcessorId,
} from '../app/models/enums';
import { PolarisFacilityInfo } from '../app/models/polarisFacilityList';

export const generateSessionId = (): string => {
    const chars =
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 9; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

export const PaymentSessionErrorMessage = 'Invalid Payment Session';

export const getJwtExpiry = (token: string) => {
    try {
        const payload = jose.decodeJwt(token);
        return payload.exp;
    } catch (err) {
        Bugsnag.notify(err as Error);
        throw new MfeException({
            statusCode: 400,
            message: 'Invalid JWT token',
        });
    }
};

export const getMfeHeight = (
    paymentMethod?: StopayPaymentMethod,
    config?: ConfigurationProps
) => {
    const defaultMfeHeight = {
        [StopayPaymentMethod.CARD]: 400,
        [StopayPaymentMethod.BACS]: 600,
        [StopayPaymentMethod.BECS]: 400,
        [StopayPaymentMethod.ACH]: 600,
        [StopayPaymentMethod.WALLET]: 400,
    };
    if (!paymentMethod) return {};
    const minMfeHeight = defaultMfeHeight[paymentMethod];
    if (paymentMethod === StopayPaymentMethod.CARD) {
        let mfeHeight = minMfeHeight;
        if (config) mfeHeight = minMfeHeight;
        return { mfeHeight, minMfeHeight };
    } else if (
        [
            StopayPaymentMethod.BACS,
            StopayPaymentMethod.BECS,
            StopayPaymentMethod.ACH,
        ].includes(paymentMethod)
    ) {
        let mfeHeight = minMfeHeight;
        if (config) mfeHeight = minMfeHeight;
        return { mfeHeight, minMfeHeight };
    }
    // Note: Need to add support for rest of the payment modes
    return { mfeHeight: minMfeHeight, minMfeHeight };
};

const bankTransferConfig: Record<CountryName, BankTransferSystemInfo> = {
    [CountryName.UK]: {
        name: BankTransferSystem.BACS,
        code: BankTransferSystem.BACS,
        transactionTypes: [
            TransactionType.BacsDebitPayment,
            TransactionType.BacsRefund,
        ],
    },
    [CountryName.USA]: {
        name: BankTransferSystem.ACH,
        code: BankTransferSystem.ACH,
        transactionTypes: [
            TransactionType.ECheckPayment,
            TransactionType.ECheckRefund,
        ],
    },
    [CountryName.AUS]: {
        name: BankTransferSystem.BECS,
        code: BankTransferSystem.BECS,
        transactionTypes: [
            TransactionType.BecsDebitPayment,
            TransactionType.BecsRefund,
        ],
    },
    [CountryName.CAN]: {
        name: BankTransferSystem.ACH,
        code: BankTransferSystem.ACH,
        transactionTypes: [
            TransactionType.ECheckPayment,
            TransactionType.ECheckRefund,
        ],
    },
};

const countryCodes = {
    [Currency.GBP]: CountryName.UK,
    [Currency.USD]: CountryName.USA,
    [Currency.AUD]: CountryName.AUS,
    [Currency.CAD]: CountryName.CAN,
};

export const getCountryCodeByCurrency = (
    currency?: Currency
): CountryName | undefined => {
    if (!currency) return undefined;
    return countryCodes[currency];
};

export const getBankTransferSystemInfo = (
    CountryName?: CountryName
): BankTransferSystemInfo => {
    if (!CountryName)
        return {
            name: BankTransferSystem.ACH,
            code: BankTransferSystem.ACH,
            transactionTypes: [
                TransactionType.ECheckPayment,
                TransactionType.ECheckRefund,
            ],
        };
    return bankTransferConfig?.[CountryName] ?? {};
};

export const resolveProcessorIdForCountry = (
    country: {
        facilities?: PolarisFacilityInfo[];
        name?: string;
        code?: string;
    } | null
): ProcessorId | null => {
    if (!country || !country.facilities?.length) {
        return null;
    }

    const processors: string[] = country.facilities
        .map((facility) => facility.activeProcessor || '')
        .filter((p): p is Processor => p !== '');

    const uniqueProcessors = Array.from(new Set<string>(processors));

    if (uniqueProcessors.length === 1) {
        const processor = uniqueProcessors[0] as Processor;
        return PROCESSOR_ID_MAP[processor];
    }
    return null;
};
